import { ReactNode } from 'react';

import { AttachmentDropZone } from '@/features/Feedback/AttachmentDropZone';
import { useAttachmentUpload } from '@/features/Feedback/useAttachmentUpload';
import { Lock } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { FEEDBACK_TEXT_MAX_LENGTH } from './feedbackLimits';

const FIELD_CLASS = 'space-y-2';

function OptionalSuffix() {
    return (
        <span className="font-normal text-muted-foreground"> (optional)</span>
    );
}

interface CardOption<T extends string> {
    value: T;
    label: string;
    hint?: string;
}

interface RadioFieldProps<T extends string> {
    label: string;
    idPrefix: string;
    options: CardOption<T>[];
    value: T | null;
    onChange: (value: T) => void;
    className: string;
}

export function RadioField<T extends string>({
    label,
    idPrefix,
    options,
    value,
    onChange,
    className,
}: RadioFieldProps<T>) {
    const groupLabelId = `${idPrefix}-label`;
    return (
        <div className="space-y-2">
            <Label id={groupLabelId} className="font-semibold">
                {label}
            </Label>
            <RadioGroup
                aria-labelledby={groupLabelId}
                value={value ?? ''}
                onValueChange={(next) => onChange(next as T)}
                className={className}
            >
                {options.map((option) => (
                    <RadioCard
                        key={option.value}
                        option={option}
                        idPrefix={idPrefix}
                        selected={option.value === value}
                        onSelect={() => onChange(option.value)}
                    />
                ))}
            </RadioGroup>
        </div>
    );
}

interface RadioCardProps<T extends string> {
    option: CardOption<T>;
    idPrefix: string;
    selected: boolean;
    onSelect: () => void;
}

/** The card is clickable as a whole; the radio is named by the title alone and described by the hint. */
function RadioCard<T extends string>({
    option,
    idPrefix,
    selected,
    onSelect,
}: RadioCardProps<T>) {
    const baseId = `${idPrefix}-${option.value}`;
    const titleId = `${baseId}-title`;
    const hintId = `${baseId}-hint`;
    return (
        // The radio inside is the keyboard target; the click handler only widens the pointer target.
        <div
            onClick={onSelect}
            className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                selected
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-300 hover:border-primary-light',
            )}
        >
            <RadioGroupItem
                value={option.value}
                id={baseId}
                aria-labelledby={titleId}
                aria-describedby={option.hint ? hintId : undefined}
                className="mt-1 shrink-0"
            />
            <span className="flex flex-col">
                <span id={titleId} className="text-sm font-semibold">
                    {option.label}
                </span>
                {option.hint && (
                    <span
                        id={hintId}
                        className="text-[13px] text-muted-foreground"
                    >
                        {option.hint}
                    </span>
                )}
            </span>
        </div>
    );
}

export function UploadField({
    label,
    hint,
    upload,
}: {
    label: string;
    hint: string;
    upload: ReturnType<typeof useAttachmentUpload>;
}) {
    return (
        <div className={FIELD_CLASS}>
            <Label>
                {label}
                <OptionalSuffix />
            </Label>
            <AttachmentDropZone
                hint={hint}
                attachments={upload.attachments}
                onFilesAdded={upload.addFiles}
                onRemove={upload.removeAttachment}
            />
        </div>
    );
}

export function ReadOnlyField({
    id,
    label,
    value,
    hint,
    icon,
}: {
    id: string;
    label: string;
    value: string;
    hint?: string;
    icon?: ReactNode;
}) {
    return (
        <div className={FIELD_CLASS}>
            <Label htmlFor={id}>{label}</Label>
            <div className="relative">
                <Input
                    id={id}
                    value={value}
                    readOnly
                    className={cn('bg-muted', icon && 'pr-12')}
                />
                {icon && (
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground">
                        {icon}
                    </span>
                )}
            </div>
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
    );
}

/** Who follows up on feedback; fixed, as in the designer's mockup. Display only. */
const FOLLOW_UP_CONTACT = 'Aoufa · aoufa@soji.ai';

export function ContactField() {
    return (
        <ReadOnlyField
            id="feedback-contact"
            label="Follow-up contact"
            value={FOLLOW_UP_CONTACT}
            hint="Questions or updates about this feedback will be sent to this address."
            icon={<Lock className="h-4 w-4" aria-hidden="true" />}
        />
    );
}

export function TextField({
    id,
    label,
    value,
    onChange,
    placeholder,
    optional = false,
}: {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    optional?: boolean;
}) {
    return (
        <div className={FIELD_CLASS}>
            <Label htmlFor={id}>
                {label}
                {optional && <OptionalSuffix />}
            </Label>
            <Textarea
                id={id}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                rows={3}
                maxLength={FEEDBACK_TEXT_MAX_LENGTH}
            />
        </div>
    );
}
